<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';

  export interface SeqResult {
    /** 元 items 配列内インデックス */
    itemIndex: number;
    text: string;
  }

  export type SeqRunState =
    | { phase: 'idle' }
    | { phase: 'running'; current: number; total: number; results: SeqResult[] }
    | { phase: 'done'; results: SeqResult[] };

  interface Props {
    open: boolean;
    /** 候補数 (= ON 項目数)。これを超える回数指定はできない */
    maxCount: number;
    /** 実行状態 */
    runState: SeqRunState;
    /** 開始要求 */
    onstart: (count: number, holdSec: number) => void;
    /** 中断要求 */
    onstop: () => void;
    /** 閉じる */
    onclose: () => void;
  }

  let { open, maxCount, runState, onstart, onstop, onclose }: Props = $props();

  let count = $state(5);
  let holdSec = $state(2);

  $effect(() => {
    if (open && runState.phase === 'idle') {
      // open になった時点で count を maxCount にクランプ
      if (count > maxCount) count = Math.max(1, maxCount);
    }
  });

  const isRunning = $derived(runState.phase === 'running');
  const isDone = $derived(runState.phase === 'done');
  const progressResults = $derived(
    runState.phase === 'running' || runState.phase === 'done' ? runState.results : [],
  );
</script>

<BottomSheet {open} title="連続実行" {onclose}>
  {#if runState.phase === 'idle'}
    <p class="hint">
      ON 項目から被りなしで複数回スピンします (現在 ON: {maxCount} 件)。
      <br />
      開始時に ON/OFF をスナップショット → 完了後に復元します。
    </p>

    <section class="field">
      <label class="lbl" for="seq-count">回数: {count}</label>
      <input
        id="seq-count"
        type="range"
        bind:value={count}
        min="1"
        max={Math.max(1, maxCount)}
        step="1"
        disabled={maxCount === 0}
      />
    </section>

    <section class="field">
      <label class="lbl" for="seq-hold">各結果の保持時間: {holdSec.toFixed(1)} 秒</label>
      <input
        id="seq-hold"
        type="range"
        bind:value={holdSec}
        min="0.5"
        max="10"
        step="0.5"
      />
    </section>

    <div class="actions">
      <button class="cancel" onclick={onclose}>キャンセル</button>
      <button
        class="primary"
        onclick={() => onstart(count, holdSec)}
        disabled={maxCount === 0 || count < 1}
      >
        開始
      </button>
    </div>
  {:else if isRunning}
    <p class="status">
      実行中: {(runState as { current: number; total: number }).current} / {(runState as { total: number }).total}
    </p>
    {#if progressResults.length > 0}
      <ul class="result-list">
        {#each progressResults as r, i (i)}
          <li><span class="num">{i + 1}.</span> {r.text}</li>
        {/each}
      </ul>
    {/if}
    <div class="actions">
      <button class="cancel" onclick={onstop}>中断</button>
    </div>
  {:else if isDone}
    <p class="status done">完了 ({progressResults.length} 件)</p>
    {#if progressResults.length > 0}
      <ul class="result-list">
        {#each progressResults as r, i (i)}
          <li><span class="num">{i + 1}.</span> {r.text}</li>
        {/each}
      </ul>
    {/if}
    <div class="actions">
      <button class="primary" onclick={onclose}>閉じる</button>
    </div>
  {/if}
</BottomSheet>

<style>
  .hint {
    margin: 0 0 var(--space-md);
    font-size: var(--font-size-sm);
    color: var(--color-fg-muted);
    line-height: 1.6;
  }
  .field {
    margin-bottom: var(--space-md);
  }
  .lbl {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-fg-muted);
    margin-bottom: var(--space-sm);
  }
  input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent);
  }
  input[type='range']:disabled {
    opacity: 0.4;
  }
  .status {
    font-size: var(--font-size-base);
    font-weight: 600;
    margin: 0 0 var(--space-md);
    color: var(--color-accent);
  }
  .status.done {
    color: var(--color-fg);
  }
  .result-list {
    list-style: none;
    margin: 0 0 var(--space-md);
    padding: 0;
    max-height: 40vh;
    overflow-y: auto;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
  }
  .result-list li {
    padding: 8px 12px;
    border-bottom: 1px solid var(--color-border);
    font-size: var(--font-size-base);
    display: flex;
    gap: 8px;
  }
  .result-list li:last-child {
    border-bottom: none;
  }
  .num {
    color: var(--color-fg-muted);
    min-width: 28px;
    font-variant-numeric: tabular-nums;
  }
  .actions {
    display: flex;
    gap: var(--space-sm);
    margin-top: var(--space-md);
  }
  .actions button {
    flex: 1;
    padding: 12px;
    border-radius: var(--radius-sm);
    font-weight: 600;
  }
  .cancel {
    background: var(--color-border);
    color: var(--color-fg);
  }
  .primary {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }
  .primary:disabled {
    opacity: 0.4;
  }
</style>
